import { useState } from 'react'
import { FaCheck, FaExclamation, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { useAnalysisRun, isTerminalStatus } from '../context/AnalysisRunContext.jsx'

// Floating list of every analysis run, collapsed to a summary pill.
//
// Rendered from Layout.jsx so it is visible on EVERY private page. Each entry
// leads with the PROJECT FOLDER name, because with several concurrent runs the
// folder is what tells the user which analysis is which — play name and file
// progress sit underneath, which also separates two runs in the same folder.
//
// Finished runs stay in this list with their action button ("View Results" /
// "Review BOM") so a completion is never lost behind a newer one.
export default function AnalysisMinimizedIndicator() {
  const {
    runList, activeRuns, finishedRuns, openTaskId,
    showRun, completeRun, closeRun, goToAssembly,
  } = useAnalysisRun()

  const [expanded, setExpanded] = useState(false)

  // Nothing to show, or the full modal is already covering this.
  if (runList.length === 0 || openTaskId) return null

  const activeCount = activeRuns.length
  const doneCount = finishedRuns.length

  const summary = [
    activeCount > 0 ? `${activeCount} running` : null,
    doneCount > 0 ? `${doneCount} complete` : null,
  ].filter(Boolean).join(' · ')

  // A single run collapses to a one-line pill; several get the expandable list.
  const showList = expanded || runList.length === 1

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 max-w-[calc(100vw-3rem)]">
      <div className="rounded-xl bg-[#1e5045] text-white shadow-xl overflow-hidden">
        {/* Summary header */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#0d5d57] transition-colors text-left"
        >
          {activeCount > 0 ? (
            <span className="w-4 h-4 flex-shrink-0 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : (
            <FaCheck className="w-4 h-4 flex-shrink-0 text-[#94c43d]" />
          )}
          <span className="flex-1 min-w-0">
            <span className="block font-medium text-sm">
              {runList.length === 1 ? 'Analysis' : `Analyses (${runList.length})`}
            </span>
            <span className="block text-xs text-white/60">{summary}</span>
          </span>
          {runList.length > 1 && (
            expanded
              ? <FaChevronDown className="w-3 h-3 flex-shrink-0 text-white/60" />
              : <FaChevronUp className="w-3 h-3 flex-shrink-0 text-white/60" />
          )}
        </button>

        {/* Per-run entries */}
        {showList && (
          <div className="max-h-80 overflow-y-auto border-t border-white/10">
            {runList.map(run => (
              <RunRow
                key={run.taskId}
                run={run}
                onOpen={() => showRun(run.taskId)}
                onComplete={() => completeRun(run.taskId)}
                onReviewBom={() => goToAssembly(run.taskId)}
                onDismiss={() => closeRun(run.taskId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function RunRow({ run, onOpen, onComplete, onReviewBom, onDismiss }) {
  const { meta, status, progress, completedResult, pendingAssemblyId } = run
  const terminal = isTerminalStatus(status)

  const folderName = meta?.folderName || 'Unknown project'
  const playName = meta?.playName || 'Analysis'
  const fileCount = meta?.fileCount ?? 0
  const pct = Math.round(progress?.progress_pct || 0)

  // Mid-run, "3 of 5 files" is more informative than the raw percentage.
  const fileProgress = (progress?.current !== undefined && progress?.total !== undefined)
    ? `${progress.current} of ${progress.total} files`
    : `${fileCount} file${fileCount !== 1 ? 's' : ''}`

  let detail
  if (status === 'SUCCESS') detail = `${playName} · ${fileCount} file${fileCount !== 1 ? 's' : ''} · complete`
  else if (status === 'FAILURE') detail = `${playName} · failed`
  else if (status === 'CANCELLED') detail = `${playName} · stopped`
  else detail = `${playName} · ${fileProgress}${pct ? ` · ${pct}%` : ''}`

  let icon
  if (status === 'SUCCESS') icon = <FaCheck className="w-3.5 h-3.5 text-[#94c43d]" />
  else if (status === 'FAILURE') icon = <FaExclamation className="w-3.5 h-3.5 text-red-400" />
  else if (status === 'CANCELLED') icon = <FaExclamation className="w-3.5 h-3.5 text-[#f26522]" />
  else icon = <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />

  return (
    <div className="px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors">
      <button onClick={onOpen} className="w-full flex items-start gap-3 text-left" title="View live logs">
        <span className="mt-0.5 flex-shrink-0">{icon}</span>
        <span className="min-w-0 flex-1">
          {/* Folder name is the primary label */}
          <span className="block text-sm font-medium truncate" title={folderName}>
            {folderName}
          </span>
          <span className="block text-xs text-white/60 truncate">{detail}</span>
          {!terminal && progress?.message && (
            <span className="block text-[11px] text-white/40 truncate mt-0.5">
              {progress.message}
            </span>
          )}
        </span>
      </button>

      {/* In-progress bar */}
      {!terminal && (
        <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-gradient-to-r from-[#f26522] to-[#94c43d] rounded-full transition-all duration-300"
            style={{ width: `${pct || 5}%` }}
          />
        </div>
      )}

      {/* Terminal-state actions, kept here so a completion is never lost */}
      {terminal && (
        <div className="flex items-center gap-2 mt-2">
          {completedResult && (
            <button
              onClick={onComplete}
              className="px-2.5 py-1 bg-white text-[#1e5045] rounded-md text-xs font-medium hover:bg-white/90 transition-colors"
            >
              View Results
            </button>
          )}
          {pendingAssemblyId && (
            <button
              onClick={onReviewBom}
              className="px-2.5 py-1 bg-white text-[#1e5045] rounded-md text-xs font-medium hover:bg-white/90 transition-colors"
            >
              Review BOM
            </button>
          )}
          <button
            onClick={onDismiss}
            className="ml-auto p-1 text-white/50 hover:text-white transition-colors"
            title="Dismiss"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}
